import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./Parentdashboard.css";

/* =========================================================
   DUMMY CHILDREN DATA
========================================================= */
const INITIAL_CHILDREN = [
  {
    id: 1,
    name: "Ravi Kumar",
    class: "10-A",
    school: "Govt. High School",
    status: "Active",
    marks: 88,
    month: "Jan",
  },
  {
    id: 2,
    name: "Neha Sharma",
    class: "12-B",
    school: "City Public School",
    status: "Active",
    marks: 92,
    month: "Feb",
  },
  {
    id: 3,
    name: "Arjun Verma",
    class: "9-C",
    school: "Saraswati Vidyalaya",
    status: "Pending",
    marks: 76,
    month: "Feb",
  },
  {
    id: 4,
    name: "Priya Singh",
    class: "11-A",
    school: "Govt. Girls School",
    status: "Active",
    marks: 95,
    month: "Mar",
  },
  {
    id: 5,
    name: "Rohit Yadav",
    class: "10-B",
    school: "City Public School",
    status: "Inactive",
    marks: 61,
    month: "Mar",
  },
  {
    id: 6,
    name: "Anjali Gupta",
    class: "12-A",
    school: "Saraswati Vidyalaya",
    status: "Active",
    marks: 89,
    month: "Apr",
  },
  {
    id: 7,
    name: "Vikram Meena",
    class: "9-A",
    school: "Govt. High School",
    status: "Pending",
    marks: 72,
    month: "Apr",
  },
  {
    id: 8,
    name: "Sneha Patel",
    class: "11-B",
    school: "Govt. Girls School",
    status: "Active",
    marks: 84,
    month: "May",
  },
  {
    id: 9,
    name: "Karan Joshi",
    class: "10-A",
    school: "City Public School",
    status: "Active",
    marks: 79,
    month: "May",
  },
  {
    id: 10,
    name: "Meera Rao",
    class: "12-C",
    school: "Saraswati Vidyalaya",
    status: "Active",
    marks: 91,
    month: "Jun",
  },
];

/* =========================================================
   MAIN PARENT DASHBOARD
========================================================= */
function Dashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [children, setChildren] = useState(INITIAL_CHILDREN);

  /* =====================================================
     PARENT STATS
  ===================================================== */
  const stats = useMemo(() => {
    const total = children.length;

    const active = children.filter(
      (child) => child.status === "Active"
    ).length;

    const pending = children.filter(
      (child) => child.status === "Pending"
    ).length;

    const inactive = children.filter(
      (child) => child.status === "Inactive"
    ).length;

    const avgMarks = total
      ? Math.round(
          children.reduce(
            (totalMarks, child) => totalMarks + child.marks,
            0
          ) / total
        )
      : 0;

    return {
      total,
      active,
      pending,
      inactive,
      avgMarks,
    };
  }, [children]);

  /* =====================================================
     SEARCH + FILTER
  ===================================================== */
  const filteredChildren = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();

    return children.filter((child) => {
      const matchSearch =
        child.name.toLowerCase().includes(search) ||
        child.school.toLowerCase().includes(search) ||
        child.class.toLowerCase().includes(search);

      const matchStatus =
        statusFilter === "All" || child.status === statusFilter;

      return matchSearch && matchStatus;
    });
  }, [children, searchTerm, statusFilter]);

  /* =====================================================
     CLASS CHART DATA
  ===================================================== */
  const classData = useMemo(() => {
    const map = {};

    children.forEach((child) => {
      map[child.class] = (map[child.class] || 0) + 1;
    });

    return Object.entries(map)
      .map(([label, value]) => ({
        label,
        value,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 7);
  }, [children]);

  /* =====================================================
     MONTHLY DATA
  ===================================================== */
  const monthlyData = useMemo(() => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];

    return months.map((month) => ({
      label: month,
      value: children.filter(
        (child) => child.month === month
      ).length,
    }));
  }, [children]);

  /* =====================================================
     PIE DATA
  ===================================================== */
  const pieData = [
    {
      label: "Active",
      value: stats.active,
      color: "#10b981",
    },
    {
      label: "Pending",
      value: stats.pending,
      color: "#f59e0b",
    },
    {
      label: "Inactive",
      value: stats.inactive,
      color: "#ef4444",
    },
  ];

  /* =====================================================
     TOP PERFORMING CHILDREN
  ===================================================== */
  const topChildren = useMemo(
    () =>
      [...children]
        .sort((a, b) => b.marks - a.marks)
        .slice(0, 5),
    [children]
  );

  /* =====================================================
     DELETE CHILD
  ===================================================== */
  const handleDelete = (id) => {
    if (window.confirm("Pakka delete karna hai?")) {
      setChildren((previous) =>
        previous.filter((child) => child.id !== id)
      );
    }
  };

  /* =====================================================
     LOGOUT
  ===================================================== */
  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    setSidebarOpen(false);
  };

  return (
    <div className="parent-dashboard-scope">
      <div
        className={`dashboard-wrapper ${
          darkMode ? "dark" : ""
        }`}
      >
        {/* =================================================
            MOBILE OVERLAY
        ================================================= */}
        {sidebarOpen && (
          <div
            className="dash-overlay"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* =================================================
            DASHBOARD LAYOUT
        ================================================= */}
        <div className="dash-layout">

          {/* =================================================
              SIDEBAR
          ================================================= */}
          <aside
            className={`dash-sidebar ${
              sidebarOpen ? "open" : ""
            }`}
          >
            {/* BRAND */}
            <div className="sidebar-brand">
              <span className="brand-icon">
                <i className="fa fa-users" />
              </span>

              <div>
                <small>PARENT</small>
                <strong>SCHOOL DARPAN</strong>
              </div>
            </div>

            {/* MENU */}
            <nav className="side-menu-wrap">
              <ul className="side-menu">

                {/* DASHBOARD */}
                <li className="active">
                  <Link
                    to="/Parentdash-board"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <i className="fa fa-tachometer" />
                    <span>Dashboard</span>
                  </Link>
                </li>

                {/* PROFILE */}
                <li>
                  <Link
                    to="/Profileparent"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <i className="fa fa-user" />
                    <span>My Profile</span>
                  </Link>
                </li>

                {/* MY CHILDREN */}
                {/* <li>
                  <Link
                    to="/My-Children"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <i className="fa fa-child" />
                    <span>My Children</span>
                  </Link>
                </li> */}

                {/* CHILD REPORT */}
                {/* <li>
                  <Link
                    to="/Student_Report_Dashbord"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <i className="fa fa-bar-chart" />
                    <span>Child Reports</span>
                  </Link>
                </li> */}

                {/* SCHOOL */}
                {/* <li>
                  <Link
                    to="/School_Report_Dashbord"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <i className="fa fa-building" />
                    <span>School Details</span>
                  </Link>
                </li> */}

                {/* FEEDBACK */}
                <li>
                  <Link
                    to="/Feedback"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <i className="fa fa-commenting" />
                    <span>Feedback</span>
                  </Link>
                </li>

                {/* DIVIDER */}
                <li
                  className="menu-divider"
                  aria-hidden="true"
                />

                {/* DARK MODE */}
                <li>
                  <button
                    type="button"
                    className="side-action-btn"
                    onClick={() =>
                      setDarkMode((previous) => !previous)
                    }
                  >
                    <i
                      className={`fa ${
                        darkMode
                          ? "fa-sun-o"
                          : "fa-moon-o"
                      }`}
                    />
                    <span>
                      {darkMode
                        ? "Light Mode"
                        : "Dark Mode"}
                    </span>
                  </button>
                </li>

                {/* LOGOUT */}
                <li>
                  <Link
                    to="/"
                    onClick={handleLogout}
                    className="logout-link"
                  >
                    <i className="fa fa-sign-out" />
                    <span>Logout</span>
                  </Link>
                </li>
              </ul>
            </nav>
          </aside>

          {/* =================================================
              MAIN
          ================================================= */}
          <main className="dash-main">

            {/* =================================================
                TOPBAR
            ================================================= */}
            <header className="dash-topbar">

              <button
                type="button"
                className="dash-menu-btn"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
              >
                <i className="fa fa-bars" />
              </button>

              <div className="topbar-title">
                <h1>Parent Dashboard</h1>

                <p>
                  Welcome back, Parent 👋
                </p>
              </div>

              <div className="topbar-actions">

                <button
                  type="button"
                  className="topbar-icon-btn"
                  title="Notifications"
                >
                  <i className="fa fa-bell" />
                  <span className="notif-dot" />
                </button>

                <button
                  type="button"
                  className="topbar-avatar"
                  title="Parent Profile"
                  onClick={() =>
                    (window.location.href = "/Profileparent")
                  }
                >
                  P
                </button>

              </div>
            </header>

            {/* =================================================
                WELCOME CARD
            ================================================= */}
            <section className="parent-welcome-card">

              <div className="welcome-content">
                <span className="welcome-badge">
                  <i className="fa fa-heart" />
                  Parent Portal
                </span>

                <h2>
                  Welcome to Your Child's
                  <span> Education Dashboard</span>
                </h2>

                <p>
                  Monitor your child's academic progress,
                  attendance, school information and
                  performance from one place.
                </p>
              </div>

              <div className="welcome-icon">
                <i className="fa fa-users" />
              </div>

            </section>

            {/* =================================================
                STATS
            ================================================= */}
            <section className="dash-stats">

              <StatCard
                color="blue"
                icon="fa-child"
                label="Total Children"
                value={stats.total}
                trend="+12% this month"
                up
              />

              <StatCard
                color="green"
                icon="fa-check-circle"
                label="Active Children"
                value={stats.active}
                trend="+5% this week"
                up
              />

              <StatCard
                color="orange"
                icon="fa-clock-o"
                label="Pending"
                value={stats.pending}
                trend="-3% this week"
                down
              />

              <StatCard
                color="purple"
                icon="fa-trophy"
                label="Average Performance"
                value={`${stats.avgMarks}%`}
                trend="+2% this month"
                up
              />

            </section>

            {/* =================================================
                CHART ROW 1
            ================================================= */}
            <section className="dash-charts">

              {/* CLASS */}
              <div className="chart-card">

                <div className="chart-header">

                  <div>
                    <h3>
                      Children by Class
                    </h3>

                    <p>
                      Class-wise distribution
                    </p>
                  </div>

                  <span className="chart-badge">
                    Live
                  </span>

                </div>

                <BarChart data={classData} />

              </div>

              {/* STATUS */}
              <div className="chart-card">

                <div className="chart-header">

                  <div>
                    <h3>
                      Child Status
                    </h3>

                    <p>
                      Current children status
                    </p>
                  </div>

                  <span className="chart-badge">
                    Live
                  </span>

                </div>

                <PieChart
                  data={pieData}
                  total={stats.total}
                />

              </div>

            </section>

            {/* =================================================
                CHART ROW 2
            ================================================= */}
            <section className="dash-charts">

              {/* MONTHLY */}
              <div className="chart-card wide">

                <div className="chart-header">

                  <div>
                    <h3>
                      Monthly Enrollment Trend
                    </h3>

                    <p>
                      Children enrollment activity
                    </p>
                  </div>

                  <span className="chart-badge success">
                    +18% growth
                  </span>

                </div>

                <LineChart data={monthlyData} />

              </div>

              {/* PERFORMANCE */}
              <div className="chart-card">

                <div className="chart-header">

                  <div>
                    <h3>
                      Overall Performance
                    </h3>

                    <p>
                      Average child performance
                    </p>
                  </div>

                </div>

                <RadialProgress
                  value={stats.avgMarks}
                />

              </div>

            </section>

            {/* =================================================
                TOP CHILDREN + ACTIVITY
            ================================================= */}
            <section className="dash-charts">

              {/* TOP CHILDREN */}
              <div className="chart-card">

                <div className="chart-header">

                  <div>
                    <h3>
                      🏆 Top Performing Children
                    </h3>

                    <p>
                      Highest academic performance
                    </p>
                  </div>

                </div>

                <ul className="top-list">

                  {topChildren.map((child, index) => (
                    <li key={child.id}>

                      <span
                        className={`rank rank-${
                          index + 1
                        }`}
                      >
                        {index + 1}
                      </span>

                      <div className="top-avatar">
                        {child.name.charAt(0)}
                      </div>

                      <div className="top-info">

                        <strong>
                          {child.name}
                        </strong>

                        <small>
                          {child.class} •{" "}
                          {child.school}
                        </small>

                      </div>

                      <span className="top-marks">
                        {child.marks}%
                      </span>

                    </li>
                  ))}

                </ul>

              </div>

              {/* ACTIVITY */}
              <div className="chart-card">

                <div className="chart-header">

                  <div>
                    <h3>
                      📅 Recent Activity
                    </h3>

                    <p>
                      Latest child activities
                    </p>
                  </div>

                </div>

                <ul className="activity-list">

                  <ActivityItem
                    icon="fa-user-plus"
                    color="blue"
                    text="Child Ravi Kumar profile updated"
                    time="2 min ago"
                  />

                  <ActivityItem
                    icon="fa-pencil"
                    color="orange"
                    text="Priya Singh's marks updated"
                    time="15 min ago"
                  />

                  <ActivityItem
                    icon="fa-check"
                    color="green"
                    text="Arjun Verma result approved"
                    time="1 hr ago"
                  />

                  <ActivityItem
                    icon="fa-calendar"
                    color="red"
                    text="Rohit Yadav attendance updated"
                    time="3 hr ago"
                  />

                  <ActivityItem
                    icon="fa-upload"
                    color="purple"
                    text="Academic report generated"
                    time="5 hr ago"
                  />

                </ul>

              </div>

            </section>

            {/* =================================================
                CHILDREN TABLE
            ================================================= */}
            <section className="dash-table-section">

              <div className="table-header">

                <div>
                  <h2>
                    My Children
                  </h2>

                  <p>
                    View and manage your children's
                    academic information
                  </p>
                </div>

                <div className="table-controls">

                  {/* SEARCH */}
                  <div className="search-box">

                    <i className="fa fa-search" />

                    <input
                      type="text"
                      placeholder="Search child..."
                      value={searchTerm}
                      onChange={(event) =>
                        setSearchTerm(
                          event.target.value
                        )
                      }
                    />

                  </div>

                  {/* FILTER */}
                  <select
                    className="filter-select"
                    value={statusFilter}
                    onChange={(event) =>
                      setStatusFilter(
                        event.target.value
                      )
                    }
                  >
                    <option value="All">
                      All Status
                    </option>

                    <option value="Active">
                      Active
                    </option>

                    <option value="Pending">
                      Pending
                    </option>

                    <option value="Inactive">
                      Inactive
                    </option>
                  </select>

                  {/* VIEW REPORT */}
                  <Link
                    // to="/Student_Report_Dashbord"
                    to=""
                    className="add-btn"
                  >
                    <i className="fa fa-bar-chart" />
                    View Reports
                  </Link>

                </div>
              </div>

              {/* TABLE */}
              <div className="table-wrapper">

                <table className="dash-table">

                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Child Name</th>
                      <th>Class</th>
                      <th>School</th>
                      <th>Marks</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>

                    {filteredChildren.length === 0 ? (
                      <tr>

                        <td
                          colSpan="7"
                          className="no-data"
                        >
                          <i className="fa fa-inbox" />

                          <p>
                            No children found
                          </p>
                        </td>

                      </tr>
                    ) : (
                      filteredChildren.map(
                        (child, index) => (
                          <tr key={child.id}>

                            <td>
                              {String(
                                index + 1
                              ).padStart(2, "0")}
                            </td>

                            <td className="name-cell">

                              <div className="avatar">
                                {child.name.charAt(
                                  0
                                )}
                              </div>

                              <span>
                                {child.name}
                              </span>

                            </td>

                            <td>
                              {child.class}
                            </td>

                            <td>
                              {child.school}
                            </td>

                            <td>
                              <strong>
                                {child.marks}%
                              </strong>
                            </td>

                            <td>

                              <span
                                className={`status-badge ${child.status.toLowerCase()}`}
                              >
                                {child.status}
                              </span>

                            </td>

                            <td className="action-cell">

                              <Link
                                // to="/Student_Report_Dashbord"
                                to=""
                                className="icon-btn edit"
                                title="View Report"
                              >
                                <i className="fa fa-eye" />
                              </Link>

                              <button
                                type="button"
                                className="icon-btn delete"
                                title="Remove"
                                onClick={() =>
                                  handleDelete(
                                    child.id
                                  )
                                }
                              >
                                <i className="fa fa-trash" />
                              </button>

                            </td>

                          </tr>
                        )
                      )
                    )}

                  </tbody>

                </table>

              </div>

            </section>

          </main>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   STAT CARD
========================================================= */
function StatCard({
  color,
  icon,
  label,
  value,
  trend,
  up,
  down,
}) {
  return (
    <div className={`stat-card ${color}`}>

      <div className="stat-icon">
        <i className={`fa ${icon}`} />
      </div>

      <div className="stat-content">

        <small>
          {label}
        </small>

        <strong>
          {value}
        </strong>

        <span
          className={`stat-trend ${
            up ? "up" : "down"
          }`}
        >
          <i
            className={`fa ${
              up
                ? "fa-arrow-up"
                : "fa-arrow-down"
            }`}
          />

          {trend}
        </span>

      </div>

    </div>
  );
}

/* =========================================================
   ACTIVITY ITEM
========================================================= */
function ActivityItem({
  icon,
  color,
  text,
  time,
}) {
  return (
    <li className="activity-item">

      <span
        className={`activity-icon ${color}`}
      >
        <i className={`fa ${icon}`} />
      </span>

      <div className="activity-info">

        <strong>
          {text}
        </strong>

        <small>
          {time}
        </small>

      </div>

    </li>
  );
}

/* =========================================================
   BAR CHART
========================================================= */
function BarChart({ data }) {
  const max = Math.max(
    ...data.map((item) => item.value),
    1
  );

  return (
    <div className="bar-chart">

      {data.map((item, index) => (
        <div
          className="bar-item"
          key={item.label}
        >

          <div className="bar-wrap">

            <div
              className="bar"
              style={{
                height: `${
                  (item.value / max) * 100
                }%`,
                animationDelay: `${
                  index * 80
                }ms`,
              }}
            >
              <span className="bar-value">
                {item.value}
              </span>
            </div>

          </div>

          <span className="bar-label">
            {item.label}
          </span>

        </div>
      ))}

    </div>
  );
}

/* =========================================================
   PIE CHART
========================================================= */
function PieChart({ data, total }) {
  const totalValue =
    data.reduce(
      (sum, item) => sum + item.value,
      0
    ) || 1;

  let cumulative = 0;

  const radius = 70;
  const cx = 90;
  const cy = 90;
  const strokeWidth = 26;

  return (
    <div className="pie-chart">

      <svg
        viewBox="0 0 180 180"
        className="pie-svg"
      >

        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />

        {data.map((item) => {

          const percentage =
            item.value / totalValue;

          const dashArray =
            2 * Math.PI * radius;

          const dashOffset =
            dashArray *
            (1 - percentage);

          const rotation =
            cumulative * 360 - 90;

          cumulative += percentage;

          return (
            <circle
              key={item.label}
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke={item.color}
              strokeWidth={strokeWidth}
              strokeDasharray={dashArray}
              strokeDashoffset={dashOffset}
              transform={`rotate(${rotation} ${cx} ${cy})`}
              strokeLinecap="round"
            />
          );
        })}

        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          className="pie-total"
        >
          {total}
        </text>

        <text
          x={cx}
          y={cy + 16}
          textAnchor="middle"
          className="pie-label"
        >
          Children
        </text>

      </svg>

      <ul className="pie-legend">

        {data.map((item) => (
          <li key={item.label}>

            <span
              className="legend-dot"
              style={{
                background: item.color,
              }}
            />

            <span>
              {item.label}
            </span>

            <strong>
              {item.value}
            </strong>

          </li>
        ))}

      </ul>

    </div>
  );
}

/* =========================================================
   LINE CHART
========================================================= */
function LineChart({ data }) {
  const width = 600;
  const height = 220;
  const padding = 40;

  const max = Math.max(
    ...data.map((item) => item.value),
    1
  );

  const points = data.map(
    (item, index) => {
      const x =
        padding +
        (index *
          (width - padding * 2)) /
          (data.length - 1);

      const y =
        height -
        padding -
        (item.value / max) *
          (height - padding * 2);

      return {
        x,
        y,
        ...item,
      };
    }
  );

  const pathD = points
    .map((point, index) =>
      index === 0
        ? `M ${point.x} ${point.y}`
        : `L ${point.x} ${point.y}`
    )
    .join(" ");

  const areaD = `${pathD}
    L ${points[points.length - 1].x}
    ${height - padding}
    L ${points[0].x}
    ${height - padding}
    Z`;

  return (
    <div className="line-chart">

      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="line-svg"
      >

        <defs>

          <linearGradient
            id="lineGradient"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop
              offset="0%"
              stopColor="#7c3aed"
              stopOpacity="0.35"
            />

            <stop
              offset="100%"
              stopColor="#7c3aed"
              stopOpacity="0"
            />
          </linearGradient>

          <linearGradient
            id="strokeGradient"
            x1="0"
            y1="0"
            x2="1"
            y2="0"
          >
            <stop
              offset="0%"
              stopColor="#2563eb"
            />

            <stop
              offset="100%"
              stopColor="#7c3aed"
            />
          </linearGradient>

        </defs>

        {[0, 0.25, 0.5, 0.75, 1].map(
          (ratio) => {

            const y =
              padding +
              ratio *
                (height - padding * 2);

            return (
              <line
                key={ratio}
                x1={padding}
                x2={width - padding}
                y1={y}
                y2={y}
                stroke="#e5e7eb"
                strokeDasharray="4 6"
              />
            );
          }
        )}

        <path
          d={areaD}
          fill="url(#lineGradient)"
        />

        <path
          d={pathD}
          fill="none"
          stroke="url(#strokeGradient)"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {points.map((point, index) => (
          <g key={index}>

            <circle
              cx={point.x}
              cy={point.y}
              r="6"
              fill="#fff"
              stroke="#7c3aed"
              strokeWidth="3"
            />

            <text
              x={point.x}
              y={point.y - 14}
              textAnchor="middle"
              className="line-point-value"
            >
              {point.value}
            </text>

            <text
              x={point.x}
              y={height - padding + 20}
              textAnchor="middle"
              className="line-label"
            >
              {point.label}
            </text>

          </g>
        ))}

      </svg>

    </div>
  );
}

/* =========================================================
   RADIAL PROGRESS
========================================================= */
function RadialProgress({ value }) {
  const radius = 70;

  const circumference =
    2 * Math.PI * radius;

  const safeValue = Math.max(
    0,
    Math.min(100, value)
  );

  const offset =
    circumference *
    (1 - safeValue / 100);

  return (
    <div className="radial-progress">

      <svg
        viewBox="0 0 180 180"
        className="radial-svg"
      >

        <defs>

          <linearGradient
            id="radialGradient"
            x1="0"
            y1="0"
            x2="1"
            y2="1"
          >
            <stop
              offset="0%"
              stopColor="#2563eb"
            />

            <stop
              offset="100%"
              stopColor="#7c3aed"
            />
          </linearGradient>

        </defs>

        <circle
          cx="90"
          cy="90"
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="14"
        />

        <circle
          cx="90"
          cy="90"
          r={radius}
          fill="none"
          stroke="url(#radialGradient)"
          strokeWidth="14"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 90 90)"
        />

        <text
          x="90"
          y="86"
          textAnchor="middle"
          className="radial-value"
        >
          {safeValue}%
        </text>

        <text
          x="90"
          y="108"
          textAnchor="middle"
          className="radial-label"
        >
          Average
        </text>

      </svg>

      <div className="radial-legend">

        <div>
          <span className="dot blue" />
          <small>Target</small>
          <strong>90%</strong>
        </div>

        <div>
          <span className="dot green" />
          <small>Achieved</small>
          <strong>
            {safeValue}%
          </strong>
        </div>

        <div>
          <span className="dot gray" />
          <small>Remaining</small>
          <strong>
            {100 - safeValue}%
          </strong>
        </div>

      </div>

    </div>
  );
}

export default Dashboard;